import React, { createContext, useContext, useState, useEffect } from 'react';
import { PALETTES, ThemePalette, getGlobalCSS } from '../constants/styles';
import { getItem } from './storageService';
import { useSettings } from './SettingsContext';

interface ThemeContextType {
  currentTheme: ThemePalette;
  themeName: string;
  setTheme: (name: string) => void;
  setCustomTheme: (theme: ThemePalette) => void;
  fontFamily: string;
}

const THEME_STORAGE_KEY = 'via_logos_theme';
const CUSTOM_THEME_STORAGE_KEY = 'via_logos_custom_theme';

// Migration map for old theme names to new theme names
const THEME_MIGRATION_MAP: Record<string, string> = {
  'CONSERVATIVE_LIGHT': 'LIGHT_CONSERVATIVE',
  'CONSERVATIVE_DARK': 'DARK_CONSERVATIVE',
  'HIGH_FANTASY_LIGHT': 'HIGH_CONTRAST_LIGHT',
  'HIGH_FANTASY_DARK': 'HIGH_CONTRAST_DARK',
  'CYBERPUNK_DARK': 'VIVID_DARK',
};

// Migrate old theme name to new theme name
const migrateThemeName = (themeName: string): string => {
  return THEME_MIGRATION_MAP[themeName] || themeName;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to convert FontFamily setting to CSS value
const getFontFamilyValue = (family: string): string => {
  switch (family) {
    case 'system':
      return 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
    case 'system-ui':
      return 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
    case 'sans-serif':
      return 'sans-serif';
    case 'serif':
      return 'serif';
    case 'monospace':
      return 'ui-monospace, Monaco, "Cascadia Code", "Roboto Mono", Courier, monospace';
    default:
      // Custom fonts - use the font name directly
      return `"${family}", system-ui, -apple-system, sans-serif`;
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fontSettings } = useSettings();
  const fontFamily = getFontFamilyValue(fontSettings.fontFamily);
  const [themeName, setThemeName] = useState('DARK_CONSERVATIVE');
  const [customTheme, setCustomThemeState] = useState<ThemePalette | null>(null);

  // Get current theme - use custom theme if selected, otherwise use built-in
  const currentTheme =
    themeName === 'CUSTOM' && customTheme
      ? customTheme
      : PALETTES[themeName] || PALETTES.DARK_CONSERVATIVE;

  // Load theme and custom theme from storage on app start
  useEffect(() => {
    const loadThemes = async () => {
      try {
        // Load selected theme name
        let savedTheme: string | null = null;
        savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

        // Load custom theme if it exists
        const savedCustomTheme = await getItem(CUSTOM_THEME_STORAGE_KEY);
        if (savedCustomTheme) {
          try {
            const parsed = JSON.parse(savedCustomTheme) as ThemePalette;
            setCustomThemeState(parsed);
          } catch (e) {
            console.error('Failed to parse custom theme:', e);
          }
        }

        // Set theme name (default to CUSTOM if custom theme exists and was last selected)
        if (savedTheme) {
          // Migrate old theme names to new ones
          const migratedTheme = migrateThemeName(savedTheme);

          if (migratedTheme === 'CUSTOM' && !savedCustomTheme) {
            // Custom theme was selected but no longer exists, fall back to default
            setThemeName('DARK_CONSERVATIVE');
          } else {
            setThemeName(migratedTheme);
          }
        } else if (savedCustomTheme) {
          // Custom theme exists but no saved preference, use it
          setThemeName('CUSTOM');
        }
      } catch (error) {
        console.error('Failed to load theme from storage', error);
      }
    };
    void loadThemes();
  }, []);

  // Sync Web CSS whenever theme changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const styleId = 'dream-catcher-styles';
      let style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }
      style.textContent = getGlobalCSS(currentTheme);
    }
  }, [currentTheme]);

  const setTheme = async (name: string) => {
    // Migrate old theme names if passed directly
    const migratedName = migrateThemeName(name);

    if (PALETTES[migratedName] || migratedName === 'CUSTOM') {
      setThemeName(migratedName);
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(THEME_STORAGE_KEY, migratedName);
        }
      } catch (error) {
        console.error('Failed to save theme to storage', error);
      }
    }
  };

  const setCustomTheme = (theme: ThemePalette) => {
    setCustomThemeState(theme);
    // Update PALETTES.CUSTOM dynamically
    PALETTES.CUSTOM = theme;
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, themeName, setTheme, setCustomTheme, fontFamily }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
